import React, { useCallback, useMemo, useState } from "react";

import { CheckCircle2, RefreshCw, Undo2 } from "lucide-react";
import { Alert } from "../../../../ui/alert";

import { EDSSpinner } from "../../../../eds/EDSSpinner";

import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";

import { ViewBooking } from "collins-js-sdk";
import {
  apiFetchNotOkHandler,
  errorHandler
} from "../../../../../APIs/errorHandler";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../../../../utils/environment";
import { Button } from "../../../../ui/button";
import {
  BookingFromApi,
  OriginalAssignment,
  ProcessedScanResult,
  ScanConfiguration,
  ScanResults
} from "../SpaceScanner.types";
import { OptimisationScore } from "../AIInsights/OptimisationScore";
import { SpaceScannerStage2BookingCard } from "./SpaceScannerStage2BookingCard";
import {
  getDependentBookingIds,
  getEmptyResultsReason
} from "./SpaceScannerStage2Controller";
import { SpaceScannerUnassignedSection } from "./SpaceScannerUnassignedSection";

type LoadingState = "idle" | "applying" | "undoing" | "rescanning";

type SpaceScannerStage2Props = {
  result: ProcessedScanResult[];
  onCompleted: (result: ScanResults) => void;
  onGoBack: () => void;
  onClose: () => void;
  scanConfig: ScanConfiguration;
  originalAssignments: OriginalAssignment[];
};

export const SpaceScannerStage2 = ({
  result,
  onCompleted,
  onGoBack,
  onClose,
  scanConfig,
  originalAssignments
}: SpaceScannerStage2Props) => {
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [processedCount, setProcessedCount] = useState<number>(0);
  const [changesApplied, setChangesApplied] = useState<boolean>(false);
  const [rescanError, setRescanError] = useState<string | undefined>(undefined);
  const diaryContext = useDiaryContext();
  const { connectionStatus } = useDeviceContext();
  const { isFlagEnabled } = useFeatureFlagContext();
  const showAICoPilot = isFlagEnabled("showAICoPilot");
  const { date, venueConfig } = diaryContext;
  const [scanResult, setScanResult] = useState<ProcessedScanResult[]>(result);
  const [appliedResults, setAppliedResults] = useState<ProcessedScanResult[]>(
    []
  );

  const totalBookings = scanResult.length;
  const totalBookingsToMove = useMemo(
    () => scanResult.filter((r: ProcessedScanResult) => r.moved).length,
    [scanResult]
  );

  const hasExcludedBookings = useMemo(() => {
    // Result types that are "permanent" exclusions - these bookings cannot be re-scanned
    const permanentExclusionTypes = [
      "booking_started",
      "booking_guestlist",
      "booking_locked",
      "booking_excluded",
      "booking_left",
      "booking_waitlisted",
      "booking_noshow",
      "booking_locked_deposit",
      "booking_locked_preorder",
      "booking_locked_manual"
    ];

    return scanResult.some(
      (r) =>
        !r.moved &&
        !permanentExclusionTypes.includes(r.result) &&
        !r.shuffleLocked
    );
  }, [scanResult]);

  const unassignedResults = useMemo(
    () => scanResult.filter((r) => r.result?.startsWith("unassigned_")),
    [scanResult]
  );

  const assignableResults = useMemo(
    () => scanResult.filter((r) => !r.result?.startsWith("unassigned_")),
    [scanResult]
  );

  const handleGoBack = useCallback(() => {
    onGoBack();
  }, [onGoBack]);

  const handleReScan = useCallback(async () => {
    if (connectionStatus === "none") {
      setRescanError(
        "An internet connection is required to re-scan. Please check your connection and try again."
      );
      return;
    }

    setLoadingState("rescanning");
    setRescanError(undefined);

    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);

    const excludedBookingIds = scanResult
      .filter((r) => !r.moved && !r.shuffleLocked)
      .map((r) => r.booking._id);

    const dateString = date.toISOString().split("T")[0];

    try {
      const excludeParam =
        excludedBookingIds.length > 0
          ? `&excludeBookings=${excludedBookingIds.join(",")}`
          : "";

      const explanationParam = showAICoPilot ? "&includeExplanation=true" : "";

      const apiResult = await fetch(
        `${environmentVars?.CORE_API_BASE_URL}/venues/${venueConfig?._id}/optimise-booking-areas?allowOverCapacity=${scanConfig.allowOverCapacity}&date=${dateString}&searchSeatedCapacity=${scanConfig.searchSeatedCapacity}${excludeParam}${explanationParam}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!apiResult.ok) {
        setLoadingState("idle");
        setRescanError("Something went wrong. Please try again.");
        apiFetchNotOkHandler(apiResult, "Error performing re-scan");
        return;
      }

      const data = (await apiResult.json()).payload.results;
      setScanResult(data);
      setLoadingState("idle");
    } catch (error) {
      setLoadingState("idle");
      setRescanError("Something went wrong. Please try again.");
      errorHandler(error, "Error performing re-scan");
    }
  }, [scanResult, date, venueConfig, scanConfig, connectionStatus]);

  const handleApply = useCallback(async () => {
    setLoadingState("applying");
    setProcessedCount(0);

    const failures: Array<{ booking: BookingFromApi; error: string }> = [];
    let processed = 0;

    for (const details of scanResult) {
      if (details.moved) {
        const oldBooking = details.booking;
        const newBooking = {
          ...oldBooking,
          id: oldBooking._id,
          assignedAreas: details.newAreas
        } as unknown as ViewBooking;

        const bookingResponse = await addOrUpdateBooking({
          booking: newBooking,
          diaryContext
        });

        if (bookingResponse?.unsaved) {
          failures.push({
            booking: oldBooking,
            error: "Failed to save booking"
          });
          details.completed = false;
          details.failed = true;
        } else {
          details.completed = true;
          details.failed = false;
        }

        processed++;
        setProcessedCount(processed);
      }
    }

    setLoadingState("idle");
    setAppliedResults([...scanResult]);
    setChangesApplied(true);

    if (failures.length > 0) {
      onCompleted({ results: [...scanResult], failures });
    }
  }, [scanResult, diaryContext, onCompleted]);

  const handleUndo = useCallback(async () => {
    setLoadingState("undoing");
    setProcessedCount(0);

    const bookingsToUndo = appliedResults.filter(
      (r) => r.moved && r.completed && !r.failed
    );
    let processed = 0;

    for (const details of bookingsToUndo) {
      const originalAssignment = originalAssignments.find(
        (a) => a.bookingId === details.booking._id
      );

      if (originalAssignment) {
        const oldBooking = details.booking;
        const revertedBooking = {
          ...oldBooking,
          id: oldBooking._id,
          assignedAreas: originalAssignment.previousAreas
        } as unknown as ViewBooking;

        await addOrUpdateBooking({
          booking: revertedBooking,
          diaryContext
        });
      }

      processed++;
      setProcessedCount(processed);
    }

    setLoadingState("idle");
    setChangesApplied(false);
    setScanResult(result);
    setAppliedResults([]);
  }, [appliedResults, originalAssignments, diaryContext, result]);

  const handleDone = useCallback(() => {
    if (appliedResults.length > 0) {
      const failures = appliedResults.filter((r) => r.failed);
      onCompleted({
        results: appliedResults,
        failures: failures.map((f) => ({
          booking: f.booking,
          error: "Failed to save booking"
        }))
      });
    } else {
      onClose();
    }
  }, [appliedResults, onCompleted, onClose]);

  const handleBookingToggle = useCallback(
    (bookingId: string, checked: boolean) => {
      if (checked) {
        setScanResult((prev) =>
          prev.map((r) => {
            if (r.booking._id === bookingId) {
              return { ...r, moved: true };
            }
            return r;
          })
        );
      } else {
        setScanResult((prev) => {
          const dependentIds = getDependentBookingIds(bookingId, prev);
          const idsToUntick = new Set([bookingId, ...dependentIds]);

          return prev.map((r) => {
            if (idsToUntick.has(r.booking._id)) {
              return { ...r, moved: false };
            }
            return r;
          });
        });
      }
    },
    []
  );

  const isLoading = loadingState !== "idle";
  const totalToUndo = appliedResults.filter(
    (r) => r.moved && r.completed && !r.failed
  ).length;

  const renderLoadingState = (): React.ReactNode => {
    const messages: Record<LoadingState, { title: string; action: string }> = {
      idle: { title: "", action: "" },
      applying: { title: "Saving bookings...", action: "processed" },
      undoing: { title: "Reverting changes...", action: "reverted" },
      rescanning: { title: "Re-scanning...", action: "" }
    };

    const total =
      loadingState === "undoing" ? totalToUndo : totalBookingsToMove;
    const message = messages[loadingState];

    return (
      <div className="flex flex-col items-center justify-center px-4 py-8 min-h-[200px]">
        <EDSSpinner size="medium" />
        <p className="mt-4 text-base font-medium text-[var(--primaryTextColor)]">
          {message.title}
        </p>
        {loadingState !== "rescanning" && (
          <>
            <p className="mt-2 text-sm text-[var(--secondaryTextColor)]">
              {processedCount} of {total} {message.action}
            </p>
            <p className="mt-1 text-sm text-[var(--secondaryTextColor)]">
              {total - processedCount} outstanding
            </p>
          </>
        )}
      </div>
    );
  };

  const renderAppliedState = (): React.ReactNode => {
    const successfulMoves = appliedResults.filter(
      (r) => r.moved && r.completed && !r.failed
    ).length;
    const failedMoves = appliedResults.filter((r) => r.failed).length;

    return (
      <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-[var(--successColor,#22c55e)]" />
        <p className="mt-4 text-base font-semibold text-[var(--primaryTextColor)] sm:text-lg">
          Changes Applied
        </p>
        {successfulMoves > 0 && (
          <Alert variant="default" className="mt-4 w-full">
            {successfulMoves} booking
            {successfulMoves !== 1 ? "s were" : " was"} successfully moved.
          </Alert>
        )}
        {failedMoves > 0 && (
          <Alert variant="destructive" className="mt-4 w-full">
            {failedMoves} booking{failedMoves !== 1 ? "s" : ""} failed to save.
          </Alert>
        )}
        <p className="mt-4 text-[13px] text-[var(--secondaryTextColor)] sm:text-sm">
          Click &quot;Done&quot; to finalise or &quot;Undo&quot; to revert all
          changes.
        </p>
      </div>
    );
  };

  const renderEmptyResults = (): React.ReactNode => {
    const reasons = getEmptyResultsReason(scanResult);

    return (
      <div className="py-4">
        <Alert variant="default" className="mb-4">
          <strong>No optimisation suggestions available</strong>
          <p className="text-sm text-[var(--secondaryTextColor)]">
            The current layout is already optimal.
          </p>
        </Alert>
        {reasons.length > 0 && (
          <div className="mt-4 rounded-lg p-4">
            <p className="mb-2 text-sm font-semibold text-[var(--primaryTextColor)]">
              Summary:
            </p>
            <ul className="m-0 list-disc pl-5 text-sm text-[var(--secondaryTextColor)] [&>li]:mb-1 [&>li:last-child]:mb-0">
              {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderResultsList = (): React.ReactNode => {
    return (
      <>
        {totalBookingsToMove === 0 ? (
          renderEmptyResults()
        ) : (
          <>
            {showAICoPilot && <OptimisationScore results={scanResult} />}
            <Alert variant="warning" className="my-4">
              {totalBookingsToMove} booking
              {totalBookingsToMove !== 1 ? "s" : ""} will be moved.{" "}
              {totalBookings - totalBookingsToMove} will stay in their current
              area.
            </Alert>
            {hasExcludedBookings && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[var(--infoBackgroundColor,#e0f2fe)] p-2.5 text-xs text-[var(--infoTextColor,#0369a1)] sm:p-3 sm:text-[13px]">
                <RefreshCw className="size-4" />
                <span>
                  Some bookings have been excluded. Click &quot;Re-Scan&quot; to
                  optimise with current selections.
                </span>
              </div>
            )}
          </>
        )}
        {rescanError && (
          <Alert variant="destructive" className="mb-4">
            {rescanError}
          </Alert>
        )}
        <SpaceScannerUnassignedSection unassignedResults={unassignedResults} />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pb-4 overflow-auto h-[300px]">
          {assignableResults.map((details) => (
            <SpaceScannerStage2BookingCard
              key={details.booking._id}
              details={details}
              onToggle={handleBookingToggle}
            />
          ))}
        </div>
      </>
    );
  };

  const renderActionButtons = (): React.ReactNode => {
    if (changesApplied) {
      return (
        <div className="flex flex-wrap items-end justify-between gap-2 pt-3 sm:gap-4 sm:pt-[var(--panelPadding)]">
          <Button
            className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base flex flex-row flex-wrap content-center justify-center items-center"
            disabled={isLoading}
            onClick={handleUndo}
            variant={"outline"}
          >
            <Undo2 className="size-4" />
            Undo
          </Button>
          <Button
            className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base"
            disabled={isLoading}
            onClick={handleDone}
            variant={"default"}
          >
            Done
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-end justify-between gap-2 pt-3 sm:gap-4 sm:pt-[var(--panelPadding)]">
        <Button
          className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base"
          disabled={isLoading}
          onClick={handleGoBack}
          variant={"outline"}
        >
          Back
        </Button>
        {hasExcludedBookings && (
          <Button
            className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base"
            disabled={isLoading}
            onClick={handleReScan}
          >
            Re-Scan
          </Button>
        )}
        <Button
          className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base"
          disabled={isLoading || totalBookingsToMove === 0}
          onClick={handleApply}
          variant={"default"}
        >
          Apply
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col h-full">
      <div className="flex items-center py-3 sm:py-[var(--panelPadding)]">
        {changesApplied ? (
          <p className="text-sm leading-relaxed sm:text-base">
            Your changes have been applied. You can undo all changes or click
            Done to finalise.
          </p>
        ) : (
          <p className="text-sm leading-relaxed sm:text-base">
            Review the changes below. Uncheck any bookings you do not want to
            move. Locked or started bookings cannot be changed.
          </p>
        )}
      </div>
      <div className="flex-1 h-0">
        <div className="mb-2">
          <div className="text-sm font-semibold text-[var(--primaryTextColor)] sm:text-base">
            {changesApplied ? "Applied Changes" : "Scan Results"}
          </div>
        </div>
        {isLoading && renderLoadingState()}
        {!isLoading && changesApplied && renderAppliedState()}
        {!isLoading && !changesApplied && renderResultsList()}
      </div>
      {renderActionButtons()}
    </div>
  );
};
